import React, {memo, ReactNode} from 'react';

import styled from 'styled-components';

import {gt} from 'app/utils/i18n';
import {getSafeAvatarUrlForUser} from 'app/utils/images';

import {BaseAvatar} from './BaseAvatar';
import {type AvatarTooltipsProps, getTooltipContent} from './tooltips';

import type {BasicMaybeTaskUserSerializedType} from './user-type';

interface Props<U extends BasicMaybeTaskUserSerializedType>
  extends AvatarTooltipsProps {
  user: U | null;
  userAvatar?: string;
  multiple?: boolean;
  displayBlock?: boolean;
  className?: string;
  style?: any;
  small?: boolean;
  isGuest?: boolean;
  onClick?: () => unknown;
}

export const BaseUserAvatar = memo<Props<BasicMaybeTaskUserSerializedType>>(
  function BaseUserAvatar<U extends BasicMaybeTaskUserSerializedType>({
    user,
    className = '',
    multiple,
    small,
    tooltipPlacement,
    tooltipTitle,
    style,
    displayBlock,
    isGuest,
    onClick,
  }: Props<U>) {
    const userAvatarImage = getSafeAvatarUrlForUser(user, 'tiny');

    const name = getUserTitle(user);

    const base = (
      <BaseAvatar
        title={name}
        image={userAvatarImage}
        displayBlock={displayBlock}
        multiple={multiple}
        className={multiple ? className : undefined}
        circle={true}
        style={style}
        tooltipPlacement={tooltipPlacement}
        tooltipContent={getTooltipContent(
          getUserTooltipItemName(
            name,
            user != null && 'is_guest_until' in user
              ? user.is_guest_until
              : undefined
          ),
          tooltipTitle
        )}
        small={small}
      />
    );

    return multiple ? (
      base
    ) : (
      <div
        onClick={onClick}
        className={`position-relative ${className}`}
        style={{lineHeight: 1}}
      >
        {base}
        {isGuest ? (
          <GuestIndicatorSpan>
            <i className="fas fa-user-clock" />
          </GuestIndicatorSpan>
        ) : null}
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.user == null || nextProps.user == null) {
      return false;
    }

    return (
      prevProps.user.id === nextProps.user.id &&
      prevProps.user.name === nextProps.user.name &&
      prevProps.user.email === nextProps.user.email &&
      prevProps.user.avatar_image === nextProps.user.avatar_image &&
      prevProps.tooltipTitle === nextProps.tooltipTitle
    );
  }
);

export function getUserTitle(
  user: BasicMaybeTaskUserSerializedType | null,
  fallback?: string
): string {
  return user?.name || user?.email || fallback || gt('Deleted Member');
}

export function getUserTooltipItemName(
  name: string | ReactNode,
  guestUntil?: string | null
): ReactNode {
  return (
    <span>
      {name}
      {guestUntil != null ? (
        <>
          {' '}
          <span className="font-italic">{gt('guest')}</span>
        </>
      ) : null}
    </span>
  );
}

const GuestIndicatorSpan = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-center',
})`
  background: white;
  color: black;
  position: absolute;
  bottom: -2px;
  right: -2px;
  font-size: 10px;
  border-radius: 50%;
  border: 1px solid #fff;
  width: 17px;
  height: 17px;
  opacity: 0.9;
  box-shadow: 0 0 3px 0px rgba(0, 0, 0, 0.25);

  > i {
    padding-bottom: 2px;
  }
`;
